import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, SearchBar, Spinner, Select } from './shared';

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'invalidated', label: 'Invalidated' },
    { value: 'all', label: 'All' },
];

function formatBytes(n) {
    if (!n) return '0';
    if (n < 1024) return `${n}`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K`;
    return `${(n / 1024 / 1024).toFixed(2)}M`;
}

function formatUsd(n) {
    const v = Number(n) || 0;
    return `$${v.toFixed(4)}`;
}

function timeLeft(expiresAt, invalidatedAt) {
    if (invalidatedAt) return 'invalidated';
    const now = Date.now();
    const exp = new Date(expiresAt).getTime();
    const diff = Math.floor((exp - now) / 1000);
    if (diff <= 0) return 'expired';
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
    return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

function StatCard({ label, value }) {
    return (
        <div className="border border-gray-200 rounded-lg bg-white p-3">
            <div className="text-[11px] text-gray-500 uppercase">{label}</div>
            <div className="text-sm font-semibold text-gray-800 mt-1">{value}</div>
        </div>
    );
}

export default function AdminGeminiCaches() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => { load(); }, [status]);

    const load = async () => {
        try {
            setLoading(true);
            const [caches, m] = await Promise.all([
                adminApi.listGeminiCaches({ status, q: searchQuery || undefined, limit: 100 }),
                adminApi.getGeminiCacheMetrics({ periodHours: 24 }),
            ]);
            setItems(caches.items || []);
            setMetrics(m);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Gemini Cache Registry</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {items.length} cache entries · real-time event stream from chat_context providers
                    </p>
                </div>
                <Button size="sm" variant="outline" onClick={load}>Refresh</Button>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            {metrics && (
                <div className="grid grid-cols-5 gap-3 mb-6">
                    <StatCard label={`Entries (${metrics.period_hours}h)`} value={metrics.totals.entries} />
                    <StatCard label="Hits" value={metrics.totals.hits} />
                    <StatCard
                        label="Cached tokens"
                        value={formatBytes(metrics.totals.cached_tokens)}
                    />
                    <StatCard
                        label="Output tokens"
                        value={formatBytes(metrics.totals.output_tokens)}
                    />
                    <StatCard label="Cost (USD)" value={formatUsd(metrics.totals.cost_usd)} />
                </div>
            )}

            <div className="flex gap-3 items-end mb-4">
                <div className="flex-1">
                    <SearchBar
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onSearch={load}
                        onRefresh={load}
                        placeholder="Search cache_key / ref..."
                    />
                </div>
                <div className="w-40">
                    <Select
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        options={STATUS_OPTIONS}
                    />
                </div>
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading caches..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No cache entries</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Cache Key</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Ctx Type</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Tokens</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Hits</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Cost</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">TTL left</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Triggered by</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((it) => (
                                <tr
                                    key={it.id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => navigate(`/admin/gemini-caches/detail?key=${encodeURIComponent(it.cache_key)}`)}
                                >
                                    <td className="px-4 py-3 font-mono text-xs max-w-md truncate">{it.cache_key}</td>
                                    <td className="px-3 py-3"><Badge variant="outline">{it.ctx_type}</Badge></td>
                                    <td className="px-3 py-3 text-right font-mono text-xs">{formatBytes(it.token_count)}</td>
                                    <td className="px-3 py-3 text-right font-mono text-xs">{it.total_hits}</td>
                                    <td className="px-3 py-3 text-right font-mono text-xs">{formatUsd(it.total_cost_usd)}</td>
                                    <td className="px-3 py-3 text-xs">
                                        <Badge
                                            variant={
                                                it.invalidated_at
                                                    ? 'danger'
                                                    : timeLeft(it.expires_at, it.invalidated_at) === 'expired'
                                                        ? 'outline'
                                                        : 'success'
                                            }
                                        >
                                            {timeLeft(it.expires_at, it.invalidated_at)}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-3 text-xs text-gray-500 font-mono">{it.triggered_by_user_id || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
